import { ActivatedRoute } from '@angular/router';
import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { DatePipe, NgClass } from '@angular/common';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { LeadAssignmentWeightHistoryPeriod, LeadAssignmentWeightRow } from 'src/app/models/potential-client.interface';

@Component({
  selector: 'app-lead-assignment',
  templateUrl: './lead-assignment.component.html',
  styleUrls: ['./lead-assignment.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, MatButton, MatFormField, MatSuffix, MatInput, MatIcon, NgClass, DatePipe],
})
export class LeadAssignmentComponent implements OnInit {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  rows: LeadAssignmentWeightRow[] = [];
  historyPeriods: LeadAssignmentWeightHistoryPeriod[] = [];
  loading = false;
  saving = false;
  error: string | null = null;
  success: string | null = null;

  private api = inject(ApiService);
  permissions = inject(PermissionsService);

  get canEdit(): boolean {
    return this.permissions.isAdmin();
  }

  get totalPercentage(): number {
    return Math.round(this.rows.reduce((sum, row) => sum + (Number(row.percentage) || 0), 0) * 100) / 100;
  }

  get isValidTotal(): boolean {
    return Math.abs(this.totalPercentage - 100) < 0.01;
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.api.getPotentialClientLeadAssignmentWeights().subscribe({
      next: data => {
        this.rows = (Array.isArray(data?.data) ? data.data : []).map(row => ({
          ...row,
          percentage: Number(row.percentage) || 0,
        }));
        this.loading = false;
      },
      error: () => {
        this.error = 'No se pudo cargar la asignación de leads.';
        this.loading = false;
      },
    });
    this.loadHistory();
  }

  loadHistory(): void {
    this.api.getPotentialClientLeadAssignmentWeightsHistory().subscribe({
      next: data => {
        this.historyPeriods = Array.isArray(data) ? data : [];
      },
      error: () => {
        this.historyPeriods = [];
      },
    });
  }

  save(): void {
    if (!this.canEdit || !this.isValidTotal) {
      return;
    }

    this.saving = true;
    this.error = null;
    this.success = null;

    this.api
      .syncPotentialClientLeadAssignmentWeights({
        weights: this.rows.map(row => ({
          user_id: row.user_id,
          percentage: Number(row.percentage) || 0,
        })),
      })
      .subscribe({
        next: data => {
          this.rows = (Array.isArray(data) ? data : []).map(row => ({
            ...row,
            percentage: Number(row.percentage) || 0,
          }));
          this.success = 'Asignación guardada.';
          this.saving = false;
          this.loadHistory();
        },
        error: err => {
          this.error = err?.error?.error ?? 'No se pudo guardar la asignación.';
          this.saving = false;
        },
      });
  }

  roleLabel(row: LeadAssignmentWeightRow): string {
    const parts: string[] = [];
    if (row.is_admin) parts.push('Admin');
    if (row.is_commercial) parts.push('Comercial');
    return parts.join(' · ') || '—';
  }

  periodLabel(period: LeadAssignmentWeightHistoryPeriod): string {
    if (period.end_date == null) {
      return 'Vigente';
    }
    return 'Cerrado';
  }
}
